import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { Image, Pressable, ScrollView, Text, View } from "react-native";
import { DiseaseTreatmentScreenProps } from "app/farmer360/routes/types";
import {
  BG_STYLES,
  BORDER_STYLES,
  Button,
  COMMON_STYLES,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import { BACK, LEAF, POWERED_BY_PLANTIX, RIGHT_ARROW } from "../assests/images";
import fetchDiseaseTreatment from "../apis/fetchDiseaseTreatment";
import {
  DiseaseDetailProps,
  Treatment,
  TreatmentTechnicalsResponse,
} from "../models/diagnosis";
import { rnNavigation } from "app/utils/appConfig";
import { ROUTES as FARMER_360_ROUTES } from "app/farmer360/routes/types";
import { useAppSelector } from "app/hooks/reduxHooks";
import { selectPlantixUId } from "app/farmer360/store/app";
import fetchDiseaseDetail from "../apis/fetchDiseaseDetail";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import { trackEvent } from "app/farmer360/utils/tracking";
import getTreatmentTechnicals from "../apis/getTreatmentTechnicals";
import TechnicalList from "../components/TechnicalList";
import { useRedirectOnRefresh } from "../hooks/useRedirectOnRefresh";
import { useTranslation } from "react-i18next";
import { CommonActions } from "@react-navigation/core";

const DiseaseTreatment = ({
  route,
  navigation,
}: DiseaseTreatmentScreenProps) => {
  const { t } = useTranslation();
  const { diseaseId, cropName, cropId } = route.params;
  const [loading, setLoading] = useState(false);
  const [treatment, setTreatment] = useState<Treatment>();
  const [disease, setDisease] = useState<DiseaseDetailProps>();
  const [technicalName, setTechnicalName] =
    useState<TreatmentTechnicalsResponse>();
  const plantixId = useAppSelector(selectPlantixUId);
  const routes = navigation.getState().routes;

  useRedirectOnRefresh({
    currentRoute: FARMER_360_ROUTES.DISEASE_TREATMENT,
    redirectRoute: FARMER_360_ROUTES.DISEASE_DIAGNOSIS,
    rnNavigation,
    skipRedirect: false,
  });

  const handleDiagnosisClick = useCallback(
    async (diseaseId: number) => {
      const response = await fetchDiseaseDetail(plantixId!, diseaseId);
      if (response) {
        setDisease(response);
      }
    },
    [plantixId],
  );

  const handleChemicalName = useCallback(async () => {
    const response = await getTreatmentTechnicals({
      diseaseId: diseaseId,
      cropId: cropId,
    });
    if (response) {
      setTechnicalName(response);
    }
  }, [cropId, diseaseId]);

  const getDiseaseTreatment = useCallback(async () => {
    setLoading(true);
    handleDiagnosisClick(diseaseId);
    handleChemicalName();
    const response = await fetchDiseaseTreatment(plantixId!, diseaseId);
    if (response) {
      setTreatment(response);
    }
    setLoading(false);
  }, [diseaseId, handleDiagnosisClick, plantixId]);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <Pressable
          style={[COMMON_STYLES["ml3/4"]]}
          onPress={() => {
            trackEvent(CustomEventTypes.BackButtonClick, {
              PageLocation: "Disease Treatment",
            });
            navigation.pop();
          }}
        >
          <Image source={BACK} style={{ width: 24, height: 24 }} />
        </Pressable>
      ),
    });
  }, [navigation]);

  useEffect(() => {
    getDiseaseTreatment();
  }, [getDiseaseTreatment]);

  useEffect(() => {
    const newRoutes = routes.filter((route) => route.name !== "StartDiagnosis");

    navigation.dispatch(
      CommonActions.reset({
        ...navigation.getState(),
        routes: newRoutes,
        index: newRoutes.length - 1,
      }),
    );
  }, [navigation, routes]);

  useEffect(() => {
    if (disease) {
      trackEvent(CustomEventTypes.CropDocImageUserLandedonTreatment, {
        PlantixID: plantixId,
        CropID: cropId,
        CropName: cropName,
        DiseaseID: diseaseId,
        DiseaseName: disease.disease_name,
      });
    }
  }, [disease, plantixId, cropId, cropName, diseaseId]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <View style={COMMON_STYLES.flex1}>
      <ScrollView>
        <View style={[COMMON_STYLES.p1, BG_STYLES.bgWhite]}>
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.justifyBetween,
              COMMON_STYLES.alignItemsCenter,
              COMMON_STYLES.mb1,
            ]}
          >
            <Text style={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.EMPHASIZED]}>
              {t("diagnosis_result")}
            </Text>
          </View>
          <View
            style={[
              COMMON_STYLES.ph1,
              COMMON_STYLES["pv3/4"],
              BG_STYLES.bgNeutral10,
              COMMON_STYLES.b1,
              BORDER_STYLES.borderNeutral20,
              COMMON_STYLES.br1,
              COMMON_STYLES.row,
              COMMON_STYLES.alignItemsCenter,
              COMMON_STYLES.justifyBetween,
            ]}
          >
            <Image
              source={disease?.image_refs[0]}
              style={{ width: 76, height: 76, borderRadius: 12 }}
            />
            <View style={COMMON_STYLES["ml3/4"]}>
              <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.SEMI_EMPHASIZED]}>
                {disease?.disease_name}
              </Text>
              <Text
                style={[
                  TYPOGRAPHY.TEXT2,
                  VARIANTS_STYLES.BASE,
                  TEXT_STYLES.textNeutral80,
                ]}
              >
                {disease?.pathogen}
              </Text>
              <Text
                style={[
                  TYPOGRAPHY.TEXT2,
                  VARIANTS_STYLES.BASE,
                  TEXT_STYLES.textNeutral80,
                ]}
              >
                {t("crop")}: {cropName}
              </Text>
            </View>
            <Pressable
              style={[
                {
                  borderRadius: 9999,
                },
              ]}
              onPress={() => {
                rnNavigation({
                  name: FARMER_360_ROUTES.DIAGNOSIS_RESULT,
                  params: {
                    diseaseId: diseaseId,
                    cropId: cropId,
                    cropName: cropName,
                  },
                });
              }}
            >
              <Image source={RIGHT_ARROW} style={{ width: 32, height: 32 }} />
            </Pressable>
          </View>
        </View>
        <View
          style={[
            COMMON_STYLES.p1,
            { borderTopWidth: 12 },
            BORDER_STYLES.borderNeutral20,
          ]}
        >
          <Text
            style={[
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.EMPHASIZED,
              COMMON_STYLES.mb1,
            ]}
          >
            {t("treatment")}
          </Text>
          <View
            style={[
              COMMON_STYLES.p1,
              BG_STYLES.bgNeutral10,
              COMMON_STYLES.b1,
              COMMON_STYLES.br1,
              BORDER_STYLES.borderNeutral20,
              COMMON_STYLES.mb1,
            ]}
          >
            <View
              style={[
                COMMON_STYLES.row,
                COMMON_STYLES.alignItemsCenter,
                COMMON_STYLES.mb1,
              ]}
            >
              <Image
                source={LEAF}
                style={[COMMON_STYLES["mr0.5"], { width: 24, height: 24 }]}
              />
              <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.SEMI_EMPHASIZED]}>
                {t("chemical_treatment")}
              </Text>
            </View>

            {technicalName && technicalName?.chemical?.length > 0 && (
              <TechnicalList items={technicalName?.chemical} />
            )}

            <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE]}>
              {treatment?.chemical_name}
            </Text>
          </View>
          <View
            style={[
              COMMON_STYLES.p1,
              BG_STYLES.bgNeutral10,
              COMMON_STYLES.b1,
              COMMON_STYLES.br1,
              BORDER_STYLES.borderNeutral20,
            ]}
          >
            <View
              style={[
                COMMON_STYLES.row,
                COMMON_STYLES.alignItemsCenter,
                COMMON_STYLES.mb1,
              ]}
            >
              <Image
                source={LEAF}
                style={[COMMON_STYLES["mr0.5"], { width: 24, height: 24 }]}
              />
              <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.SEMI_EMPHASIZED]}>
                {t("organic_measures")}
              </Text>
            </View>

            {technicalName && technicalName?.biological?.length > 0 && (
              <TechnicalList items={technicalName?.biological} />
            )}

            <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE]}>
              {treatment?.organic_name}
            </Text>
          </View>
        </View>
        <View
          style={[
            COMMON_STYLES.p1,
            { borderTopWidth: 12 },
            BORDER_STYLES.borderNeutral20,
          ]}
        >
          <Text
            style={[
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.EMPHASIZED,
              COMMON_STYLES.mb1,
            ]}
          >
            {t("management")}
          </Text>
          <View
            style={[
              COMMON_STYLES.p1,
              BG_STYLES.bgNeutral10,
              COMMON_STYLES.b1,
              COMMON_STYLES.br1,
              BORDER_STYLES.borderNeutral20,
            ]}
          >
            <View
              style={[
                COMMON_STYLES.row,
                COMMON_STYLES.alignItemsCenter,
                COMMON_STYLES.mb1,
              ]}
            >
              <Image
                source={LEAF}
                style={[COMMON_STYLES["mr0.5"], { width: 24, height: 24 }]}
              />
              <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.SEMI_EMPHASIZED]}>
                {t("preventive_measures")}
              </Text>
            </View>
            {treatment?.prevention.map((prevention) => {
              return (
                <View
                  style={[
                    COMMON_STYLES.row,
                    COMMON_STYLES.alignItemsCenter,
                    COMMON_STYLES["mb3/4"],
                  ]}
                >
                  <Text
                    style={{ fontSize: 20, lineHeight: 24, marginRight: 6 }}
                  >
                    •
                  </Text>
                  <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE]}>
                    {prevention}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>
      </ScrollView>
      <View
        style={[
          COMMON_STYLES.p1,
          {
            shadowColor: "#000",
            shadowOffset: { width: 0, height: -3 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 5,
          },
        ]}
      >
        <Button
          onClick={() => {
            rnNavigation({ name: FARMER_360_ROUTES.DISEASE_DIAGNOSIS });
          }}
          text={t("start_new_diagnosis")}
          size={SIZE.large}
        />
      </View>
    </View>
  );
};

export default DiseaseTreatment;
